import type { AgentEvent } from "./agent-events.ts";
import type { ChatEvent } from "./models.ts";

/**
 * Chat history reads (K2): query modes for `GET /api/chats/:id/events` and the
 * delta elision shared by REST pages and WS replay.
 */

/** `?afterSeq=&limit=` default and ceiling. */
export const CHAT_EVENTS_DEFAULT_LIMIT = 500;
export const CHAT_EVENTS_MAX_LIMIT = 1_000;
/** Ceiling on `tailTurns` and `turns`. */
export const CHAT_EVENTS_MAX_TURNS = 50;
/** A turn-bounded read scans at most this many of the newest events. When the
 * cap cuts a turn, the page starts at the cap with `hasMoreBefore: true`. */
export const CHAT_EVENTS_TURN_SCAN_CAP = 2_000;

export type ChatEventsQuery =
  /** `?afterSeq=&limit=`: the next events after a cursor. */
  | { mode: "after"; afterSeq: number; limit: number }
  /** `?tailTurns=N`: the newest N turns. */
  | { mode: "tail"; tailTurns: number }
  /** `?beforeSeq=S&turns=N`: N turns ending before S (exclusive). */
  | { mode: "before"; beforeSeq: number; turns: number };

export type ChatEventsQueryResult = { ok: true; query: ChatEventsQuery } | { ok: false; error: string };

type QuerySource = URLSearchParams | Record<string, string | undefined>;

function read(source: QuerySource, name: string): string | undefined {
  if (source instanceof URLSearchParams) return source.get(name) ?? undefined;
  return source[name];
}

function turnCount(value: string, name: string): number | string {
  const count = Number(value);
  return Number.isInteger(count) && count >= 1 && count <= CHAT_EVENTS_MAX_TURNS
    ? count
    : `${name} must be an integer from 1 to ${CHAT_EVENTS_MAX_TURNS}`;
}

/** Parses the three mutually exclusive history modes. `afterSeq`/`limit` keep
 * their historical leniency (junk numbers fall back to the defaults); the
 * turn modes are strict. */
export function parseChatEventsQuery(source: QuerySource): ChatEventsQueryResult {
  const afterSeq = read(source, "afterSeq");
  const limit = read(source, "limit");
  const tailTurns = read(source, "tailTurns");
  const beforeSeq = read(source, "beforeSeq");
  const turns = read(source, "turns");
  const after = afterSeq !== undefined || limit !== undefined;

  if (tailTurns !== undefined) {
    if (after || beforeSeq !== undefined || turns !== undefined) return { ok: false, error: "tailTurns cannot be combined with other history parameters" };
    const count = turnCount(tailTurns, "tailTurns");
    return typeof count === "string" ? { ok: false, error: count } : { ok: true, query: { mode: "tail", tailTurns: count } };
  }
  if (beforeSeq !== undefined || turns !== undefined) {
    if (after) return { ok: false, error: "beforeSeq cannot be combined with afterSeq or limit" };
    if (beforeSeq === undefined || turns === undefined) return { ok: false, error: "beforeSeq and turns go together" };
    const before = Number(beforeSeq);
    if (!Number.isSafeInteger(before) || before < 1) return { ok: false, error: "beforeSeq must be a positive integer" };
    const count = turnCount(turns, "turns");
    return typeof count === "string" ? { ok: false, error: count } : { ok: true, query: { mode: "before", beforeSeq: before, turns: count } };
  }
  return {
    ok: true,
    query: {
      mode: "after",
      afterSeq: Number(afterSeq ?? "0") || 0,
      limit: Math.min(Number(limit ?? String(CHAT_EVENTS_DEFAULT_LIMIT)) || CHAT_EVENTS_DEFAULT_LIMIT, CHAT_EVENTS_MAX_LIMIT),
    },
  };
}

/** The query string (without `?`) for a history mode. */
export function formatChatEventsQuery(query: ChatEventsQuery): string {
  switch (query.mode) {
    case "after":
      return `afterSeq=${query.afterSeq}&limit=${query.limit}`;
    case "tail":
      return `tailTurns=${query.tailTurns}`;
    case "before":
      return `beforeSeq=${query.beforeSeq}&turns=${query.turns}`;
  }
}

/** Anything with the fields elision reads: confirmed or provisional events. */
export type ElidableChatEvent = Pick<ChatEvent, "jobId" | "turnId" | "payload">;

type AgentEventOf<T extends AgentEvent["type"]> = Extract<AgentEvent, { type: T }>;

function textEvent<T extends "text.delta" | "text.done">(event: ElidableChatEvent, type: T): AgentEventOf<T> | undefined {
  return event.payload.kind === "agent" && event.payload.event.type === type
    ? event.payload.event as AgentEventOf<T>
    : undefined;
}

/**
 * Drops `text.delta` events that a later `text.done` in the same page makes
 * redundant. Every history page and every `subscribe.chat` replay goes
 * through it; live frames never do. The caller reports the scanned range as
 * `coverage`.
 *
 * A delta is dropped only when all of these hold:
 *  - its preceding event in this page establishes a new text block;
 *  - it has a `jobId` and a `turnId`;
 *  - it belongs to a run of consecutive `text.delta` events of that same
 *    job and turn, and the event immediately after the run is a `text.done`
 *    of the same job and turn — so every event between the delta and the
 *    `text.done` is a delta of that job/turn;
 *  - that `text.done` is the main agent's (no `parentToolUseId`); and
 *  - the run's text and the `text.done` text are prefix-compatible, exactly
 *    the condition under which both clients' folds replace the streamed block
 *    with the `text.done` text.
 *
 * Adjacency is stricter than "no other event of that job/turn in between": an
 * interleaved event from another job or turn changes which block the clients'
 * folds merge into, so eliding across it would change what they draw.
 * `thinking.delta` is never elided: nothing closes a thinking block.
 *
 * Folding the result gives the same transcript text as folding the input. The
 * input must be in seq order; it is not modified.
 */
export function elideSupersededDeltas<E extends ElidableChatEvent>(events: readonly E[]): E[] {
  const output: E[] = [];
  let index = 0;
  while (index < events.length) {
    const first = events[index]!;
    if (!textEvent(first, "text.delta") || first.jobId === undefined || first.turnId === undefined) {
      output.push(first);
      index++;
      continue;
    }
    let end = index;
    let runText = "";
    while (end < events.length) {
      const candidate = events[end]!;
      const delta = textEvent(candidate, "text.delta");
      if (!delta || candidate.jobId !== first.jobId || candidate.turnId !== first.turnId) break;
      runText += delta.text;
      end++;
    }
    const closing = events[end];
    const done = closing ? textEvent(closing, "text.done") : undefined;
    // A page can start halfway through an open block. Fold-neutral events
    // (turn.started, usage, tool updates, etc.) do not establish a new block
    // either. Only elide after a known block boundary inside this page.
    const previous = events[index - 1]?.payload;
    const completeRun = previous?.kind === "user.message"
      || (previous?.kind === "agent" && (
        previous.event.type === "thinking.delta"
        || previous.event.type === "tool.started"
      ));
    const elide = completeRun && done !== undefined
      && closing!.jobId === first.jobId
      && closing!.turnId === first.turnId
      && done.parentToolUseId === undefined
      && (done.text.startsWith(runText) || runText.startsWith(done.text));
    if (!elide) for (let keep = index; keep < end; keep++) output.push(events[keep]!);
    index = end;
  }
  return output;
}
